import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import NavigationSidebar from '../../components/navigation/NavigationSidebar';
import UserProfileDropdown from '../../components/navigation/UserProfileDropdown';
import NotificationCenter from '../../components/navigation/NotificationCenter';
import BreadcrumbNavigation from '../../components/navigation/BreadcrumbNavigation';
import Icon from '../../components/AppIcon';
import KPICard from './components/KPICard';
import RevenueTrendChart from './components/RevenueTrendChart';
import ConversionRateChart from './components/ConversionRateChart';
import PerformanceComparisonChart from './components/PerformanceComparisonChart';
import ExportPanel from './components/ExportPanel';
import DateRangeFilter from './components/DateRangeFilter';
import { getAnalyticsData } from '../../services/supabaseService';

const AnalyticsDashboard = () => {
  const navigate = useNavigate();
  const { t, currentLanguage } = useLanguage();
  const { profile } = useAuth();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [dateRange, setDateRange] = useState({
    startDate: new Date(new Date().setMonth(new Date().getMonth() - 6))?.toISOString()?.split('T')?.[0],
    endDate: new Date()?.toISOString()?.split('T')?.[0]
  });

  useEffect(() => {
    loadAnalyticsData();
  }, [dateRange]);

  const loadAnalyticsData = async () => {
    try {
      setLoading(true);
      const data = await getAnalyticsData(dateRange?.startDate, dateRange?.endDate);
      setAnalyticsData(data);
    } catch (err) {
      console.error('Error loading analytics data:', err);
    } finally {
      setLoading(false);
    }
  };

  const breadcrumbItems = [
    { label: t('nav.dashboard'), path: '/agency-dashboard' },
    { label: t('analytics.title'), path: '/analytics-dashboard' }
  ];

  return (
    <div className="flex h-screen bg-background">
      <NavigationSidebar 
        isCollapsed={isSidebarCollapsed} 
        onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)} 
      />
      
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <header className="relative bg-card border-b border-border px-4 md:px-6 py-3 md:py-4 flex items-center justify-between z-10">
          <div className="flex items-center gap-3 md:gap-4">
            <button
              onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
              className="p-2 hover:bg-muted rounded-lg transition-smooth lg:hidden"
            >
              <Icon name="Menu" size={20} className="text-foreground" />
            </button>
            <div>
              <h1 className="text-xl md:text-2xl font-semibold text-foreground">
                {t('analytics.title')}
              </h1>
              <BreadcrumbNavigation items={breadcrumbItems} />
            </div>
          </div>
          <div className="flex items-center gap-2 md:gap-3">
            <NotificationCenter 
              onNotificationClick={() => {}}
              onMarkAsRead={() => {}}
            />
            <UserProfileDropdown 
              user={profile}
              onLogout={() => navigate('/logout')}
              onLanguageChange={() => {}}
            />
          </div>
        </header>

        {/* Main Content */}
        <main className="flex-1 overflow-y-auto p-3 sm:p-4 md:p-6 space-y-4 md:space-y-6">
          {/* Date Range Filter & Export */}
          <div className="flex flex-col sm:flex-row gap-3 md:gap-4 items-stretch sm:items-center justify-between">
            <DateRangeFilter 
              dateRange={dateRange}
              onChange={setDateRange}
            />
            <ExportPanel 
              analyticsData={analyticsData}
              dateRange={dateRange}
              loading={loading}
              onExportPDF={() => {}}
              onExportCSV={() => {}}
            />
          </div>

          {/* KPI Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            <KPICard
              title={t('analytics.pilgrimConversionRate')}
              value={analyticsData?.conversionRate ? `${analyticsData?.conversionRate}%` : '0%'}
              change={analyticsData?.conversionRateChange}
              changeType={analyticsData?.conversionRateChange?.includes('+') ? 'positive' : 'negative'}
              icon="TrendingUp"
              iconColor="text-primary"
              iconBgColor="bg-primary/10"
              trend={analyticsData?.conversionRateChange?.includes('+') ? 'up' : 'down'}
              loading={loading}
            />
            <KPICard
              title={t('analytics.avgRevenuePerPilgrim')}
              value={analyticsData?.avgRevenuePerPilgrim ? `${analyticsData?.avgRevenuePerPilgrim?.toLocaleString()} MAD` : '0 MAD'}
              change={analyticsData?.avgRevenueChange}
              changeType={analyticsData?.avgRevenueChange?.includes('+') ? 'positive' : 'negative'}
              icon="DollarSign"
              iconColor="text-success"
              iconBgColor="bg-success/10"
              trend={analyticsData?.avgRevenueChange?.includes('+') ? 'up' : 'down'}
              loading={loading}
            />
            <KPICard
              title={t('analytics.documentCompletionRate')}
              value={analyticsData?.documentCompletionRate ? `${analyticsData?.documentCompletionRate}%` : '0%'}
              change={analyticsData?.documentCompletionChange}
              changeType={analyticsData?.documentCompletionChange?.includes('+') ? 'positive' : 'negative'}
              icon="FileCheck"
              iconColor="text-secondary"
              iconBgColor="bg-secondary/10"
              trend={analyticsData?.documentCompletionChange?.includes('+') ? 'up' : 'down'}
              loading={loading}
            />
            <KPICard
              title={t('analytics.seasonalPerformance')}
              value={analyticsData?.seasonalPerformance || 'N/A'}
              change={analyticsData?.seasonalChange}
              changeType={analyticsData?.seasonalChange?.includes('+') ? 'positive' : 'negative'}
              icon="Calendar"
              iconColor="text-warning"
              iconBgColor="bg-warning/10"
              trend={analyticsData?.seasonalChange?.includes('+') ? 'up' : 'down'}
              loading={loading}
            />
          </div>

          {/* Revenue Trend Chart */}
          <RevenueTrendChart 
            data={analyticsData?.revenueTrend || []}
            loading={loading}
          />

          {/* Conversion Rate & Performance Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
            <ConversionRateChart 
              data={analyticsData?.conversionData || []}
              loading={loading}
            />
            <PerformanceComparisonChart 
              data={analyticsData?.performanceComparison || []}
              loading={loading}
            />
          </div>
        </main>
      </div>
    </div>
  );
};

export default AnalyticsDashboard;